// Web-based audio recorder using MediaRecorder API

let mediaRecorder: MediaRecorder | null = null;
let audioChunks: Blob[] = [];
let startTime: number = 0;

const WebAudioRecorder = {
  async startRecording({ format }: { format: string }): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Try to use AAC if supported, otherwise fall back to webm
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunks = [];
      startTime = Date.now();

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      console.log("[WebAudioRecorder] Recording started");
    } catch (error) {
      console.error("[WebAudioRecorder] Failed to start recording:", error);
      throw error;
    }
  },

  async stopRecording(): Promise<{
    file: Blob;
    audioUri: string;
    duration: number;
    fileType: string;
    path: string;
    mimeType: string;
    size: number;
    filename: string;
  }> {
    return new Promise((resolve, reject) => {
      if (!mediaRecorder) {
        reject(new Error("No active recording"));
        return;
      }

      mediaRecorder.onstop = () => {
        const duration = (Date.now() - startTime) / 1000; // Duration in seconds
        const mimeType = mediaRecorder?.mimeType || 'audio/webm';
        const extension = mimeType.includes('webm') ? 'webm' : 'aac';
        const blob = new Blob(audioChunks, { type: mimeType });
        const filename = `recording_${Date.now()}.${extension}`;

        // Stop all tracks to release microphone
        mediaRecorder?.stream.getTracks().forEach(track => track.stop());

        console.log("[WebAudioRecorder] Recording stopped, duration:", duration);

        resolve({
          file: blob,
          audioUri: URL.createObjectURL(blob),
          duration,
          fileType: extension,
          path: filename,
          mimeType,
          size: blob.size,
          filename,
        });

        mediaRecorder = null;
        audioChunks = [];
      };

      mediaRecorder.onerror = (event) => {
        console.error("[WebAudioRecorder] Recording error:", event);
        reject(new Error("Recording failed"));
      };

      mediaRecorder.stop();
    });
  },

  isRecording(): boolean {
    return mediaRecorder?.state === "recording";
  },

  async requestPermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch {
      return false;
    }
  },
};

export default WebAudioRecorder;
